/**
 * Admin e-card / product thumbnail for Ops and customer scheduled-smiles lists.
 *
 * Delegates still vs MP4 rules to {@link EcardTemplateThumbnail} so admin lists
 * stay aligned with picker tiles and confirmation shells.
 */

import { type ReactNode } from 'react';
import { ThemeIcon, Tooltip } from '@mantine/core';
import { IconPhotoOff } from '@tabler/icons-react';
import { brandColors } from '../../config/theme';
import { EcardTemplateThumbnail } from '../common/EcardTemplateThumbnail';
import {
  ecardMediaSourceFromAdminThumbnail,
  type EcardTemplateMediaSource,
} from '../../utils/ecardTemplateMediaUrls';

export type AdminEcardThumbnailProps = {
  /** When true, show privacy placeholder (no network fetch). */
  hidden: boolean;
  /** /api/media/{token} — image still or video blob when isVideo. */
  mediaToken: string | null | undefined;
  /** Absolute/external still URL (preview image, flower catalog, poster still). */
  externalUrl: string | null | undefined;
  /** When true, mediaToken is a video — render muted video thumb. */
  isVideo?: boolean;
  width: number;
  height: number;
  /** Accessible label for the media. */
  alt: string;
  /** Shown when there is no media or load fails. */
  fallback: ReactNode;
};

/**
 * Renders catalog-safe admin thumbnail imagery (or a privacy / empty fallback).
 */
export const AdminEcardThumbnail = ({
  hidden,
  mediaToken,
  externalUrl,
  isVideo = false,
  width,
  height,
  alt,
  fallback,
}: AdminEcardThumbnailProps) => {
  if (hidden) {
    return (
      <Tooltip label="User-uploaded image hidden for privacy">
        <ThemeIcon
          size={Math.min(width, height)}
          variant="light"
          color={brandColors.neutral}
          radius="md"
          aria-label="User-uploaded image hidden for privacy"
        >
          <IconPhotoOff size={Math.round(Math.min(width, height) * 0.4)} />
        </ThemeIcon>
      </Tooltip>
    );
  }

  const source: EcardTemplateMediaSource = ecardMediaSourceFromAdminThumbnail({
    mediaToken,
    externalUrl,
    isVideo,
  });

  const hasMedia = !!(mediaToken?.trim() || externalUrl?.trim());

  if (!hasMedia) {
    return <>{fallback}</>;
  }

  return (
    <EcardTemplateThumbnail
      source={source}
      alt={alt}
      width={width}
      height={height}
      fit="contain"
      radius={4}
      fallback={fallback}
    />
  );
};
